import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Car, Plus, Trash2, Edit2, AlertTriangle, Shield, Calendar,
  ChevronRight, Package, Clock, CheckCircle2, XCircle, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mi Garaje - Device Registry Page
const MiGarajePage = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  
  const [formData, setFormData] = useState({
    device_name: '',
    brand: '',
    model: '',
    serial_number: '',
    imei: '',
    purchase_date: '',
    purchase_source: '',
    warranty_end_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchDevices();
    fetchAlerts();
  }, []);

  const fetchDevices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/devices`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/warranty-alerts`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDevice 
        ? `${API_URL}/api/user/devices/${editingDevice.device_id}`
        : `${API_URL}/api/user/devices`;
      
      const res = await fetch(url, {
        method: editingDevice ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingDevice ? 'Dispositivo actualizado' : 'Dispositivo añadido');
        setShowAddDialog(false);
        setEditingDevice(null);
        resetForm();
        fetchDevices();
      } else {
        toast.error('Error al guardar dispositivo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('¿Eliminar este dispositivo de tu garaje?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/user/devices/${deviceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Dispositivo eliminado');
        fetchDevices();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      device_name: '',
      brand: '',
      model: '',
      serial_number: '',
      imei: '',
      purchase_date: '',
      purchase_source: '',
      warranty_end_date: '',
      notes: ''
    });
  };

  const openEditDialog = (device) => {
    setEditingDevice(device);
    setFormData({
      device_name: device.device_name || '',
      brand: device.brand || '',
      model: device.model || '',
      serial_number: device.serial_number || '',
      imei: device.imei || '',
      purchase_date: device.purchase_date || '',
      purchase_source: device.purchase_source || '',
      warranty_end_date: device.warranty_end_date?.split('T')[0] || '',
      notes: device.notes || ''
    });
    setShowAddDialog(true);
  };

  const getWarrantyBadge = (device) => {
    if (!device.warranty_status) return null;
    
    if (device.warranty_status === 'active') {
      return (
        <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
          <Shield className="w-3 h-3" />
          {device.warranty_days_left} días restantes
        </span>
      );
    }
    
    return (
      <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
        <XCircle className="w-3 h-3" />
        Garantía expirada
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="mi-garaje-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Car className="w-8 h-8 text-[#0047FF]" />
              Mi Garaje
            </h1>
            <p className="text-gray-600 mt-1">
              Gestiona todos tus dispositivos registrados
            </p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#0047FF] hover:bg-[#003BCC]"
                onClick={() => { resetForm(); setEditingDevice(null); }}
                data-testid="add-device-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir Dispositivo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingDevice ? 'Editar Dispositivo' : 'Añadir Dispositivo'}
                </DialogTitle>
                <DialogDescription>
                  Registra tu dispositivo para seguimiento de garantías y reparaciones
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Nombre del dispositivo *</label>
                    <Input
                      required
                      placeholder="Mi PS5"
                      value={formData.device_name}
                      onChange={(e) => setFormData({...formData, device_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Marca *</label>
                    <Input
                      required
                      placeholder="Sony"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Modelo *</label>
                    <Input
                      required
                      placeholder="PlayStation 5"
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Nº Serie / IMEI</label>
                    <Input
                      placeholder="ABC123456"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Fecha de compra *</label>
                    <Input
                      type="date"
                      required
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Lugar de compra</label>
                    <Input
                      placeholder="Amazon, FNAC..."
                      value={formData.purchase_source}
                      onChange={(e) => setFormData({...formData, purchase_source: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Fin de garantía (estimado)</label>
                  <Input
                    type="date"
                    value={formData.warranty_end_date}
                    onChange={(e) => setFormData({...formData, warranty_end_date: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Notas</label>
                  <Input
                    placeholder="Notas adicionales..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#0047FF]">
                    {editingDevice ? 'Guardar Cambios' : 'Añadir Dispositivo'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowAddDialog(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Warranty Alerts */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl"
            data-testid="warranty-alerts"
          >
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-yellow-800">Alertas de Garantía</h3>
            </div>
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div 
                  key={alert.device_id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    alert.urgency === 'high' ? 'bg-red-100' : 
                    alert.urgency === 'medium' ? 'bg-yellow-100' : 'bg-orange-50'
                  }`}
                >
                  <div>
                    <p className="font-medium">{alert.device_name}</p>
                    <p className="text-sm text-gray-600">{alert.brand} {alert.model}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${
                      alert.urgency === 'high' ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {alert.days_left} días
                    </p>
                    <p className="text-xs text-gray-500">hasta fin de garantía</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Devices Grid */}
        {devices.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Tu garaje está vacío
            </h3>
            <p className="text-gray-500 mb-6">
              Añade tus dispositivos para hacer seguimiento de garantías y reparaciones
            </p>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-[#0047FF]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir mi primer dispositivo
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devices.map((device) => (
              <motion.div
                key={device.device_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                data-testid={`device-card-${device.device_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{device.device_name}</h3>
                    <p className="text-sm text-gray-500">{device.brand} {device.model}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditDialog(device)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(device.device_id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Warranty Badge */}
                <div className="mb-4">
                  {getWarrantyBadge(device)}
                </div>

                {/* Device Info */}
                <div className="space-y-2 text-sm">
                  {device.serial_number && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Nº Serie:</span>
                      <span className="font-mono">{device.serial_number}</span>
                    </div>
                  )}
                  {device.purchase_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Comprado:</span>
                      <span>{new Date(device.purchase_date).toLocaleDateString('es-ES')}</span>
                    </div>
                  )}
                  {device.purchase_source && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tienda:</span>
                      <span>{device.purchase_source}</span>
                    </div>
                  )}
                </div>

                {/* Repair History */}
                {device.repair_history?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Historial de reparaciones:</p>
                    <div className="space-y-1">
                      {device.repair_history.slice(0, 2).map((repair) => (
                        <Link
                          key={repair.request_id}
                          to={`/warranty/${repair.request_id}`}
                          className="flex items-center gap-2 text-xs text-[#0047FF] hover:underline"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          {repair.status} - {new Date(repair.created_at).toLocaleDateString('es-ES')}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Link to="/warranty/new">
                    <Button variant="outline" size="sm" className="w-full">
                      Solicitar reparación
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiGarajePage;
